import React from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useMemo } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import { OrganizationGroupSelections } from 'libs/end-to-end/src/group-entitlements';
import { CircularProgress } from '@mui/material';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { AreYouSureDialog } from 'apps/sigma2-ui/src/components/AreYouSureDialog';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const GroupsPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const language = useLanguage();

  const [deleteGroupPrompt, setDeleteGroupPrompt] = useState<string | null>(
    null,
  );

  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const getUrlFn = useCallback(() => `${getApiBaseUrl()}/groups`, []);

  const readGroupsResult = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: OrganizationGroupSelections,
  });

  const isLoading = readGroupsResult.status === 'loading';
  const isError = readGroupsResult.status === 'error';

  const onDeleteGroup = useCallback((groupId: string) => {
    setDeleteGroupPrompt(groupId);
  }, []);

  const onCancelDeleteGroup = useCallback(() => {
    setDeleteGroupPrompt(null);
  }, []);

  const refreshGroups = useCallback(() => {
    setDeleteGroupPrompt(null);

    if (readGroupsResult.status === 'loading') {
      return;
    }

    readGroupsResult.reloadEntity();
  }, [readGroupsResult]);

  const onConfirmDeleteGroup = useAsyncCallback(
    async (isAlive: IsAliveFn, groupId: string) => {
      if (!isAlive() || !myUser) {
        return;
      }

      setIsDeletingGroup(true);
      try {
        const deleteResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/groups/${groupId}`,
          requestOptions: {
            method: 'DELETE',
          },
          responseSchema: EmptyObject,
        });

        if (isAlive()) {
          if (deleteResult && deleteResult.resultType === 'ok') {
            refreshGroups();
          }
        }
      } finally {
        if (isAlive()) {
          setIsDeletingGroup(false);
          setDeleteGroupPrompt(null);
        }
      }
    },
    [myUser, refreshGroups, userAuthenticatedApiCall],
  );

  const rows: AdminTableRow[] = useMemo(() => {
    if (isLoading || isError || readGroupsResult.status !== 'done') {
      return [];
    }

    return readGroupsResult.entity.groups.map((group) => {
      return {
        key: group.id,
        columns: [
          {
            columnType: 'text',
            text: group.name,
            key: 'name',
          },
          {
            columnType: 'text',
            text: group.organization.name,
            key: 'organizationName',
          },
          {
            columnType: 'custom',
            key: 'actions',
            ui: (
              <Spacing direction="vertical" size="md">
                <Button
                  behavior={{
                    type: 'href',
                    href: `/groups/${group.id}/entitlements`,
                  }}
                >
                  {convertTranslatedContentToReactNode(
                    language.editGroupEntitlementsButton(),
                  )}
                </Button>
                <Button
                  behavior={{
                    type: 'href',
                    href: `/groups/${group.id}/users`,
                  }}
                >
                  {convertTranslatedContentToReactNode(
                    language.manageGroupUsersButton(),
                  )}
                </Button>
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      onDeleteGroup(group.id);
                    },
                  }}
                >
                  {convertTranslatedContentToReactNode(
                    language.deleteGroupButton(),
                  )}
                </Button>
                {deleteGroupPrompt === group.id && (
                  <AreYouSureDialog
                    onCancel={onCancelDeleteGroup}
                    onSure={() => {
                      onConfirmDeleteGroup(group.id);
                    }}
                    bodyTranslatedContent={language.groupDeleteAreYouSureBody({
                      groupName: group.name,
                    })}
                    disableInputs={isDeletingGroup}
                  />
                )}
              </Spacing>
            ),
          },
        ],
      };
    });
  }, [
    isLoading,
    isError,
    readGroupsResult,
    language,
    deleteGroupPrompt,
    onCancelDeleteGroup,
    onConfirmDeleteGroup,
    isDeletingGroup,
    onDeleteGroup,
  ]);

  return (
    <AdminLayout currentPage={'Groups'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Groups', to: '/groups' },
        ]}
      />
      {isLoading && (
        <CircularProgress
          className="text-secondary"
          color="inherit"
          size={96}
          thickness={8}
        />
      )}
      {!isLoading && isError && (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={language.unknownError()}
          />
        </Spacing>
      )}
      {!isLoading && !isError && (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Typography
            variant={'headline-4'}
            translatedContent={language.groupsPageTitle()}
          />

          <Button
            behavior={{
              type: 'href',
              href: '/groups/new',
            }}
          >
            {convertTranslatedContentToReactNode(language.createGroupButton())}
          </Button>
          <AdminTable
            headings={['Group Name', 'Organization Name', 'Actions']}
            rows={rows}
          />
        </Spacing>
      )}
    </AdminLayout>
  );
};
